import { getActiveShops } from "@/utils/storeAddress.js";

export const EMPTY_SHOP_PHONE = {
    phoneNumber: "",
    whatsappEnabled: false
};

/** Compare phones by digits only (spaces/punctuation ignored). */
export function phoneDigitsKey(phoneNumber) {
    return String(phoneNumber || "").replace(/\D/g, "");
}

export function normalizeStorePhone(phone) {
    if (!phone) {
        return { ...EMPTY_SHOP_PHONE };
    }
    if (typeof phone === "string") {
        return {
            phoneNumber: phone,
            whatsappEnabled: true
        };
    }
    return {
        phoneNumber: phone.phoneNumber || "",
        whatsappEnabled: phone.whatsappEnabled === true
    };
}

export function normalizeShopPhones(phones) {
    if (!phones?.length) {
        return [{ ...EMPTY_SHOP_PHONE }];
    }
    return phones.map((phone) => normalizeStorePhone(phone));
}

export function buildShopPhonePayload(phones) {
    return (phones || [])
        .map((phone) => ({
            phoneNumber: phone.phoneNumber.trim(),
            whatsappEnabled: phone.whatsappEnabled === true
        }))
        .filter((phone) => phone.phoneNumber);
}

/**
 * Keep first occurrence of each phone (digit identity). Mutates `seen` when provided.
 * Used for the header only, where space is limited and phones lack shop context.
 */
export function uniquePhonesByDigits(phones, seen = new Set()) {
    const unique = [];
    for (const phone of phones || []) {
        const normalized = normalizeStorePhone(phone);
        const key = phoneDigitsKey(normalized.phoneNumber);
        if (!key || seen.has(key)) {
            continue;
        }
        seen.add(key);
        unique.push(normalized);
    }
    return unique;
}

export function getGeneralStorePhones(storeInfo) {
    return (storeInfo?.phones || [])
        .map((phone) => normalizeStorePhone(phone))
        .filter((phone) => phone.phoneNumber);
}

/** Header phone list: general + shop phones, deduped by digits. */
export function collectDisplayedPhones(storeInfo) {
    const shops = getActiveShops(storeInfo?.shops);
    const multipleShops = shops.length > 1;
    const seen = new Set();
    const entries = [];

    uniquePhonesByDigits(getGeneralStorePhones(storeInfo), seen).forEach((phone) => {
        entries.push({
            ...phone,
            shopName: null,
            key: `general-${phoneDigitsKey(phone.phoneNumber)}`
        });
    });

    shops.forEach((shop) => {
        uniquePhonesByDigits(shop.phones, seen).forEach((normalized) => {
            entries.push({
                ...normalized,
                shopName: multipleShops ? shop.name : null,
                key: `${shop.id || shop.name}-${phoneDigitsKey(normalized.phoneNumber)}`
            });
        });
    });

    return entries;
}

export function hasDisplayedPhones(storeInfo) {
    return collectDisplayedPhones(storeInfo).length > 0;
}
